// src/services/admin.service.js
import { collection, query, where, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "@/firebase";
import { getCurrentUser } from "@/services/auth.service";
import { Logger } from "@/utils/logger";
import { EnvironmentConfig } from "@/config/environment";
import { FeatureFlags } from "@/config/features";
import { isTestModeAllowed } from "@/config/testMode";

/**
 * Check if the current user has admin privileges
 * @returns {Promise<boolean>} Promise that resolves to true if user is an admin, false otherwise
 */
export const isAdmin = async () => {
  try {
    // Check for Cypress test mode (build-gated; never active in production)
    if (isTestModeAllowed()) {
      if (window.__mockAdminService) {
        Logger.log(
          "debug",
          "Using mock admin service in test mode",
          null,
          "AdminService"
        );
        return await window.__mockAdminService.isAdmin();
      }

      // Fallback to checking mock user data directly
      const mockUser = window.__getAuthState ? window.__getAuthState() : null;
      if (mockUser) {
        const isUserAdmin = mockUser.isAdmin === true;
        Logger.log(
          "debug",
          "Checking mock user admin status",
          { userId: mockUser.uid, isAdmin: isUserAdmin },
          "AdminService"
        );
        return isUserAdmin;
      }

      return false;
    }

    const currentUser = auth.currentUser;

    if (!currentUser) {
      Logger.log(
        "debug",
        "No current user found for admin check",
        null,
        "AdminService"
      );
      return false;
    }

    Logger.log(
      "debug",
      "Checking admin status for user",
      { userId: currentUser.uid },
      "AdminService"
    );

    const tokenResult = await currentUser.getIdTokenResult();
    const isUserAdmin = tokenResult.claims.admin === true;

    Logger.log(
      "info",
      "Admin status check completed",
      { userId: currentUser.uid, isAdmin: isUserAdmin },
      "AdminService"
    );

    return isUserAdmin;
  } catch (error) {
    Logger.log(
      "error",
      "Error checking admin status",
      { error: error.message, userId: auth.currentUser?.uid },
      "AdminService"
    );
    return false;
  }
};

/**
 * Get a list of users with admin privileges
 * @returns {Promise<Array>} Promise that resolves to an array of admin users
 */
export const getAdminUsers = async () => {
  Logger.log("info", "Fetching admin users list", null, "AdminService");

  try {
    if (!FeatureFlags.admin.userManagement) {
      throw new Error("User management feature is disabled");
    }

    // First check if current user is an admin
    const adminStatus = await isAdmin();
    if (!adminStatus) {
      Logger.log(
        "error",
        "Unauthorized attempt to view admin users",
        { userId: getCurrentUser()?.uid },
        "AdminService"
      );
      throw new Error("Unauthorized: Only admins can view other admins");
    }

    // Query for users with admin privileges
    const q = query(collection(db, "users"), where("isAdmin", "==", true));

    const querySnapshot = await getDocs(q);
    const adminUsers = [];

    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      adminUsers.push({
        id: doc.id,
        name: userData.name,
        email: userData.email,
        userType: userData.userType,
        isAdmin: userData.isAdmin,
        createdAt: userData.createdAt,
      });
    });

    Logger.log(
      "info",
      "Successfully retrieved admin users",
      { count: adminUsers.length },
      "AdminService"
    );

    return adminUsers;
  } catch (error) {
    Logger.log(
      "error",
      "Error getting admin users",
      { error: error.message },
      "AdminService"
    );
    throw error;
  }
};

/**
 * Set admin status for a user
 * @param {string} userId - User ID to update
 * @param {boolean} adminStatus - New admin status
 * @returns {Promise<void>}
 */
export const setAdminStatus = async (userId, adminStatus) => {
  Logger.log(
    "info",
    "Setting admin status for user",
    { userId, adminStatus },
    "AdminService"
  );

  try {
    if (!FeatureFlags.admin.userManagement) {
      throw new Error("User management feature is disabled");
    }

    // First check if current user is an admin
    const isCurrentUserAdmin = await isAdmin();
    if (!isCurrentUserAdmin) {
      Logger.log(
        "error",
        "Unauthorized attempt to modify admin status",
        {
          targetUserId: userId,
          currentUserId: getCurrentUser()?.uid,
          requestedStatus: adminStatus,
        },
        "AdminService"
      );
      throw new Error("Unauthorized: Only admins can modify admin status");
    }

    // Prevent self-demotion in production
    const currentUser = getCurrentUser();
    if (
      EnvironmentConfig.isProduction &&
      currentUser?.uid === userId &&
      !adminStatus
    ) {
      Logger.log(
        "error",
        "Attempted self-demotion in production",
        { userId },
        "AdminService"
      );
      throw new Error(
        "Cannot remove admin privileges from yourself in production"
      );
    }

    const setAdminClaimFn = httpsCallable(functions, "setAdminClaim");
    await setAdminClaimFn({ uid: userId, adminStatus });

    Logger.log(
      "info",
      "Successfully updated admin status",
      {
        userId,
        adminStatus,
        updatedBy: currentUser?.uid,
      },
      "AdminService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error setting admin status",
      {
        error: error.message,
        userId,
        adminStatus,
      },
      "AdminService"
    );
    throw error;
  }
};
